import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { GoogleIcon } from '../components/GoogleIcon';

/**
 * Only let the `?next=` redirect target match internal paths — never an
 * external URL or javascript:. This is the open-redirect guard.
 */
function safeNext(raw: string | null, fallback: string): string {
  if (!raw) return fallback;
  if (!raw.startsWith('/') || raw.startsWith('//')) return fallback;
  return raw;
}

export function SignInPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const [search] = useSearchParams();
  const next = safeNext(search.get('next'), '/dashboard');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [magicSent, setMagicSent] = useState(false);

  // If you're already signed in, ricochet to the next target.
  if (auth.user) {
    navigate(next, { replace: true });
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (error) {
      setErr(error.message);
      return;
    }
    navigate(next, { replace: true });
  }

  async function onGoogle() {
    setErr(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
      },
    });
    if (error) setErr(error.message);
  }

  async function onMagicLink() {
    setErr(null);
    if (!email) {
      setErr('Enter your email first.');
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth/callback?next=${encodeURIComponent(next)}`,
      },
    });
    setLoading(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setMagicSent(true);
  }

  return (
    <div className="max-w-md mx-auto px-6 py-16 md:py-20">
      <div className="eyebrow mb-4">Sign in</div>
      <h1 className="editorial-h1 mb-3">
        Welcome <em className="italic text-accent">back.</em>
      </h1>
      <p className="editorial-deck mb-10">
        Sign in to continue your CYBHI checklist where you left off.
      </p>

      <button
        type="button"
        onClick={onGoogle}
        className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-ink text-ink text-sm font-label hover:bg-paper-deep transition-colors mb-3"
      >
        <GoogleIcon />
        Continue with Google
      </button>

      <div className="flex items-center gap-3 my-7">
        <div className="flex-1 h-px bg-rule" />
        <span className="font-label text-[10px] tracking-[0.2em] uppercase text-ink-muted">
          or with email
        </span>
        <div className="flex-1 h-px bg-rule" />
      </div>

      {magicSent ? (
        <div className="border-l-2 border-accent pl-5 py-4 font-editorial italic text-[16px] text-ink-soft leading-relaxed">
          Magic link sent to <strong className="font-medium not-italic text-ink">{email}</strong>.
          Open it on this device to sign in.
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            label="Email"
            type="email"
            value={email}
            onChange={setEmail}
            autoComplete="email"
          />
          <Field
            label="Password"
            type="password"
            value={password}
            onChange={setPassword}
            autoComplete="current-password"
          />

          {err && <ErrorRow message={err} />}

          <div className="pt-2 flex flex-wrap items-center justify-between gap-4">
            <button
              type="button"
              onClick={onMagicLink}
              className="font-label text-[11px] uppercase tracking-[0.15em] text-link underline underline-offset-2 hover:text-accent"
            >
              email me a magic link instead
            </button>
            <button type="submit" disabled={loading} className="btn-text">
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </div>
        </form>
      )}

      <div className="mt-10 pt-6 border-t border-rule font-editorial italic text-[15px] text-ink-muted">
        New here?{' '}
        <Link
          to={`/signup${next !== '/dashboard' ? `?next=${encodeURIComponent(next)}` : ''}`}
          className="text-link underline underline-offset-2 hover:text-accent not-italic"
        >
          Create an account
        </Link>
      </div>
    </div>
  );
}

function Field({
  label,
  type = 'text',
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
}) {
  return (
    <label className="block">
      <span className="font-label text-[10px] font-medium uppercase tracking-[0.18em] text-ink-muted block mb-1.5">
        {label}
      </span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
        className="w-full bg-transparent border-0 border-b border-rule focus:border-accent focus:outline-none font-editorial text-[17px] text-ink py-2"
      />
    </label>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div className="font-editorial italic text-[14px] text-[#a02525] flex items-start gap-2">
      <span aria-hidden="true">⚠</span>
      <span>{message}</span>
    </div>
  );
}
